import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { Enrolment } from "./models/Enrolment";
import { Progress } from "./models/Progress";
import { CurriculumTemplate, Programme, ProgrammeSummary } from "./models/Programme";
import { SessionTopicPlan, TopicRef } from "./models/TopicPlan";
import {
  addTopic,
  archiveTopic,
  createProgramme,
  enrolMember,
  fetchCurriculumTemplates,
  fetchMemberEnrolments,
  fetchProgramme,
  fetchProgrammes,
  removeEnrolment,
  updateEnrolment,
  updateProgramme,
  updateTopic,
} from "./slice-extensions/training-slice-extension";
import {
  fetchEnrolmentProgress,
  fetchTopicChoices,
  saveTopicPlanDraft,
} from "./slice-extensions/training-topics-slice-extension";
import { removeEnrolmentSignature, signEnrolment } from "./slice-extensions/training-signature-slice-extension";

/**
 * The session form's unsaved topic plan. The form saves it (saveTopicPlanDraft) once the
 * session save returned the id; `sessionId` is null for a session not created yet.
 */
export interface TopicPlanDraft {
  sessionId: string | null;
  topics: TopicRef[];
  anchorDate: string | null;
  hadPlan: boolean;
  dirty: boolean;
}

interface TrainingState {
  templates: CurriculumTemplate[];
  programmes: ProgrammeSummary[];
  programmesStatus: LoadingStatus;
  selectedProgramme: Programme | null;
  enrolmentsByMember: Record<string, Enrolment[]>;
  topicChoices: TopicRef[];
  progressByEnrolment: Record<string, Progress>;
  topicPlanDraft: TopicPlanDraft | null;
  topicPlanSaveError: string | null;
  lastError?: ApiErrorResponse;
}

const initialState: TrainingState = {
  templates: [],
  programmes: [],
  programmesStatus: LoadingStatus.Idle,
  selectedProgramme: null,
  enrolmentsByMember: {},
  topicChoices: [],
  progressByEnrolment: {},
  topicPlanDraft: null,
  topicPlanSaveError: null,
  lastError: undefined,
};

const toSummary = (programme: Programme, enrolledCount: number): ProgrammeSummary => ({
  id: programme.id,
  name: programme.name,
  licenceClass: programme.licenceClass,
  isLicenceClassProgramme: programme.isLicenceClassProgramme,
  templateCode: programme.templateCode,
  templateVersion: programme.templateVersion,
  isArchived: programme.isArchived,
  enrolledCount,
});

/** A saved programme replaces the open one and refreshes its row in the list. */
const applyProgramme = (state: TrainingState, programme: Programme): void => {
  state.selectedProgramme = programme;
  const index = state.programmes.findIndex((p) => p.id === programme.id);
  if (index >= 0) {
    state.programmes[index] = toSummary(programme, state.programmes[index].enrolledCount);
  } else {
    state.programmes = [...state.programmes, toSummary(programme, 0)].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }
};

const draftFrom = (sessionId: string | null, plan: SessionTopicPlan | null): TopicPlanDraft => ({
  sessionId,
  topics: plan?.topics ?? [],
  anchorDate: plan?.anchorDate ?? null,
  hadPlan: plan != null,
  dirty: false,
});

const replaceEnrolment = (state: TrainingState, enrolment: Enrolment): void => {
  const list = state.enrolmentsByMember[enrolment.memberId] ?? [];
  const exists = list.some((e) => e.id === enrolment.id);
  state.enrolmentsByMember[enrolment.memberId] = exists
    ? list.map((e) => (e.id === enrolment.id ? enrolment : e))
    : [...list, enrolment];
};

const trainingSlice = createSlice({
  name: "training",
  initialState,
  reducers: {
    clearTrainingError: (state) => {
      state.lastError = undefined;
    },
    /** Starts editing a session's topics from its saved plan (null = none). */
    startTopicPlanDraft: (
      state,
      action: PayloadAction<{ sessionId: string | null; plan: SessionTopicPlan | null }>
    ) => {
      state.topicPlanDraft = draftFrom(action.payload.sessionId, action.payload.plan);
      state.topicPlanSaveError = null;
    },
    setTopicPlanDraftTopics: (state, action: PayloadAction<TopicRef[]>) => {
      const draft = state.topicPlanDraft ?? draftFrom(null, null);
      state.topicPlanDraft = { ...draft, topics: action.payload, dirty: true };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurriculumTemplates.fulfilled, (state, action: PayloadAction<CurriculumTemplate[]>) => {
        state.templates = action.payload;
      })
      .addCase(fetchProgrammes.pending, (state) => {
        state.programmesStatus = LoadingStatus.Loading;
      })
      .addCase(fetchProgrammes.fulfilled, (state, action: PayloadAction<ProgrammeSummary[]>) => {
        state.programmesStatus = LoadingStatus.Idle;
        state.programmes = action.payload;
      })
      .addCase(fetchProgrammes.rejected, (state, action) => {
        state.programmesStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })
      .addCase(fetchProgramme.fulfilled, (state, action) => {
        state.selectedProgramme = action.payload;
      })
      .addCase(createProgramme.fulfilled, (state, action) => applyProgramme(state, action.payload))
      .addCase(updateProgramme.fulfilled, (state, action) => applyProgramme(state, action.payload))
      .addCase(addTopic.fulfilled, (state, action) => applyProgramme(state, action.payload))
      .addCase(updateTopic.fulfilled, (state, action) => applyProgramme(state, action.payload))
      .addCase(archiveTopic.fulfilled, (state, action) => applyProgramme(state, action.payload))
      .addCase(fetchMemberEnrolments.fulfilled, (state, action) => {
        state.enrolmentsByMember[action.payload.memberId] = action.payload.enrolments;
      })
      .addCase(enrolMember.fulfilled, (state, action) => replaceEnrolment(state, action.payload.enrolment))
      .addCase(updateEnrolment.fulfilled, (state, action) => replaceEnrolment(state, action.payload))
      .addCase(signEnrolment.fulfilled, (state, action) => replaceEnrolment(state, action.payload))
      .addCase(removeEnrolmentSignature.fulfilled, (state, action) => replaceEnrolment(state, action.payload))
      .addCase(removeEnrolment.fulfilled, (state, action) => {
        const { memberId, enrolmentId } = action.payload;
        state.enrolmentsByMember[memberId] = (state.enrolmentsByMember[memberId] ?? []).filter(
          (e) => e.id !== enrolmentId
        );
      })
      .addCase(fetchTopicChoices.fulfilled, (state, action) => {
        state.topicChoices = action.payload;
      })
      .addCase(fetchEnrolmentProgress.fulfilled, (state, action) => {
        state.progressByEnrolment[action.payload.enrolmentId] = action.payload;
      })
      .addCase(saveTopicPlanDraft.fulfilled, (state, action) => {
        state.topicPlanSaveError = null;
        if (!state.topicPlanDraft?.dirty) return;
        state.topicPlanDraft = draftFrom(action.meta.arg.sessionId, action.payload);
      })
      .addCase(saveTopicPlanDraft.rejected, (state, action) => {
        // Kept dirty and re-keyed to the saved session, so its edit page shows the unsaved topics.
        if (state.topicPlanDraft) state.topicPlanDraft.sessionId = action.meta.arg.sessionId;
        state.topicPlanSaveError = action.payload?.message ?? "Failed to save the topics";
      });
  },
});

export const { clearTrainingError, startTopicPlanDraft, setTopicPlanDraftTopics } = trainingSlice.actions;

export const selectCurriculumTemplates = (state: RootState): CurriculumTemplate[] => state.training.templates;
export const selectProgrammes = (state: RootState): ProgrammeSummary[] => state.training.programmes;
export const selectProgrammesStatus = (state: RootState): LoadingStatus => state.training.programmesStatus;
export const selectSelectedProgramme = (state: RootState): Programme | null => state.training.selectedProgramme;

const NO_ENROLMENTS: Enrolment[] = [];
export const selectMemberEnrolments =
  (memberId: string) =>
  (state: RootState): Enrolment[] =>
    state.training.enrolmentsByMember[memberId] ?? NO_ENROLMENTS;

/** Whether the member's enrolments have arrived at least once (the card's loading state). */
export const selectMemberEnrolmentsLoaded =
  (memberId: string) =>
  (state: RootState): boolean =>
    state.training.enrolmentsByMember[memberId] !== undefined;

export const selectTopicChoices = (state: RootState): TopicRef[] => state.training.topicChoices;
export const selectTopicPlanDraft = (state: RootState): TopicPlanDraft | null => state.training.topicPlanDraft;
export const selectTopicPlanSaveError = (state: RootState): string | null => state.training.topicPlanSaveError;
export const selectProgressByEnrolment = (state: RootState): Record<string, Progress> =>
  state.training.progressByEnrolment;

export default trainingSlice.reducer;
